import type { Artifact } from '../types'
import { IconDownload, IconFile, IconReport } from '../icons'

/**
 * 产物清单。
 *
 * 背景：这条管线除了成品图还会产出诊断板、叠瓦热力图、验收对照板与 sidecar，
 *   它们是「凭什么说修好了」的证据。PRD 第 2.2 节定了不做历史记录库，
 *   页面上这一屏就是取证据的地方，所以用户必须能一次把它们全部拿走。
 *   （产物本身不再限时清理：2026-09-10 用户裁决，登录用户的作业一直留着。）
 * 设计意图：**几颗按钮，不是一张清单**。要成品的人点第一颗，要交代的人点报告，
 *   要自己核的人点过程数据，谁都不必先读完十几个文件名。
 *
 *   2026-09-11 用户裁决：「分成直接下载产物和下载全部过程数据」「你不要搞这种瀑布流」。
 *   在此之前证据类产物平铺成方格，十三个格子占了一屏半——那一屏看起来像文件管理器，
 *   而它要回答的问题其实只有一个：我的图呢。
 *
 *   过程数据在服务端打成一个 zip（`/jobs/{id}/bundle.zip`，原图也在里面）。
 *   逐个下载会触发浏览器「是否允许下载多个文件」的拦截，而且拿回去还得自己对名字。
 * 关键约束：文件名走服务端白名单匹配，前端不拼路径。这里的 name 只做展示与
 *   传给下载接口，不参与任何路径组装。
 */
export function ArtifactList({
  artifacts,
  onDownload,
  onDownloadAll,
  onReport,
}: {
  artifacts: Artifact[]
  onDownload: (a: Artifact) => void
  /** 打包下载全部过程数据。 */
  onDownloadAll: () => void
  /** 下载这一趟的报告。 */
  onReport: () => void
}) {
  const primary = artifacts.find(a => a.primary)
  const rest = artifacts.filter(a => !a.primary)

  return (
    <div className="arts-two">
      {primary && (
        <button className="art-main" onClick={() => onDownload(primary)}>
          <div className="art-main-ic"><IconDownload size={20} /></div>
          <div className="art-main-tx">
            <b>下载成品图</b>
            <span>{primary.name}</span>
          </div>
        </button>
      )}
      {/* 报告排在成品旁边，过程数据退到下面一整行：三种人里，要「拿去跟人交代」的
          比要「自己逐个核」的多得多，而那个 ZIP 拆开是十几个只有我们看得懂的文件。 */}
      <button className="art-main art-main-alt" onClick={onReport}>
        <div className="art-main-ic"><IconReport size={20} /></div>
        <div className="art-main-tx">
          <b>下载处理报告</b>
          <span>{'诊断读数、决策轨迹、验收与逐像素差，附件逐个给 sha256（HTML，可打印）'}</span>
        </div>
      </button>
      {rest.length > 0 && (
        <button className="art-main art-main-alt art-main-wide" onClick={onDownloadAll}>
          <div className="art-main-ic"><IconFile size={20} /></div>
          <div className="art-main-tx">
            <b>下载全部过程数据</b>
            {/* 说清楚里面是什么：不说的话，这颗按钮看着像「再下一次成品」。 */}
            <span>{`原图、诊断板、热力图、验收对照与运行记录，共 ${rest.length + 2} 个文件（ZIP）`}</span>
          </div>
        </button>
      )}
    </div>
  )
}

/**
 * 产物清单的固定构成。
 *
 * 背景：管线产出哪些文件是确定的，但文件名带图片原名前缀，
 *   所以清单在运行时按 stem 拼出来。
 * 设计意图：把「有哪些产物、各自叫什么」集中在一处，
 *   前端别处只消费这个列表，避免多处硬编码文件名。
 */
export function buildArtifacts(stem: string, opts: { regen: boolean }): Artifact[] {
  const list: Artifact[] = [
    { name: `${stem}_restored.png`, label: '成品图', kind: 'image', primary: true },
    { name: `${stem}_input_diag_board.png`, label: '诊断板（处理前）', kind: 'image' },
    { name: `${stem}_input_scaleheat.png`, label: '叠瓦热力图', kind: 'image' },
    { name: `${stem}_verify_board.png`, label: '验收对照板', kind: 'image' },
  ]
  if (opts.regen) {
    list.push({ name: `${stem}_regen1_refclean.png`, label: '参考级净化本', kind: 'image' })
    list.push({ name: `${stem}_regen1_diag_board.png`, label: '诊断板（重画之后）', kind: 'image' })
  }
  list.push({ name: `${stem}_restored.json`, label: '运行记录与校验和', kind: 'json' })
  list.push({ name: `${stem}_trace.xml`, label: '决策轨迹', kind: 'xml' })
  return list
}
